// src/pages/creator-panel/CallLogs/components/WaveformPlayer.js
// Custom canvas-based stereo waveform with HTML5 audio control.
// No wavesurfer.js dependency — the API pre-computes amplitude peaks at
// finalize and serves them as { left: [...], right: [...], bins }. This
// keeps the bundle small and skips client-side audio decoding.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Button, Typography, Tooltip, useTheme } from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';

// Channel colors are an intentional VAPI-brand convention and stay constant
// across themes — orange = assistant, teal = customer.
const ASSISTANT_COLOR = '#f97316';
const CUSTOMER_COLOR = '#14b8a6';

const TICK_INTERVAL = 30; // seconds between tick labels

const fmt = (s) => {
    const sec = Math.max(0, Math.floor(s));
    const m = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${m}:${ss.toString().padStart(2, '0')}`;
};

const WaveformPlayer = ({ audioUrl, audioUrlLoading, audioUrlError, durationSeconds, peaks, onRequestAudioUrl }) => {
    const theme = useTheme();
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const playheadRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(durationSeconds || 0);

    // ----- Canvas waveform render -----
    const drawWave = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.clientWidth;
        const cssH = canvas.clientHeight;
        if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
            canvas.width = cssW * dpr;
            canvas.height = cssH * dpr;
            ctx.scale(dpr, dpr);
        }
        ctx.clearRect(0, 0, cssW, cssH);

        const left = (peaks && peaks.left) || [];   // assistant — top channel
        const right = (peaks && peaks.right) || []; // customer — bottom channel
        const bins = Math.max(left.length, right.length, 1);

        const midY = cssH / 2;
        const channelH = (cssH / 2) - 6; // small gap between channels

        const barW = Math.max(1, cssW / bins);
        const barGap = Math.max(0.5, barW * 0.2);
        const innerBarW = barW - barGap;

        // Top channel — assistant (orange), drawn upward from midline
        ctx.fillStyle = ASSISTANT_COLOR;
        for (let i = 0; i < left.length; i++) {
            const x = i * barW;
            const h = (left[i] || 0) * channelH;
            ctx.fillRect(x, midY - h, innerBarW, h);
        }
        // Bottom channel — customer (teal), drawn downward from midline
        ctx.fillStyle = CUSTOMER_COLOR;
        for (let i = 0; i < right.length; i++) {
            const x = i * barW;
            const h = (right[i] || 0) * channelH;
            ctx.fillRect(x, midY, innerBarW, h);
        }

        // Midline divider + tick marks — driven by theme so they read on both light/dark
        ctx.fillStyle = theme.palette.divider;
        ctx.fillRect(0, midY, cssW, 0.5);

        if (duration > 0) {
            ctx.fillStyle = theme.palette.text.secondary;
            ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
            for (let t = TICK_INTERVAL; t < duration; t += TICK_INTERVAL) {
                const x = (t / duration) * cssW;
                ctx.fillRect(x, midY - 3, 0.5, 6);
                ctx.fillText(fmt(t), x + 4, midY + 14);
            }
        }
    }, [peaks, duration, theme]);

    // Redraw on size or peaks change
    useEffect(() => {
        drawWave();
        const onResize = () => drawWave();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [drawWave]);

    // Playhead animation
    useEffect(() => {
        let rafId;
        const tick = () => {
            const audio = audioRef.current;
            const playhead = playheadRef.current;
            const canvas = canvasRef.current;
            if (audio && playhead && canvas && duration > 0) {
                const ratio = audio.currentTime / duration;
                playhead.style.left = `${Math.min(100, Math.max(0, ratio * 100))}%`;
                setCurrentTime(audio.currentTime);
            }
            if (playing) rafId = requestAnimationFrame(tick);
        };
        if (playing) rafId = requestAnimationFrame(tick);
        return () => { if (rafId) cancelAnimationFrame(rafId); };
    }, [playing, duration]);

    // Audio element wiring
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onLoaded = () => {
            if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
        };
        const onEnded = () => setPlaying(false);
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
        };
    }, [audioUrl]);

    const handlePlayPause = async () => {
        if (!audioUrl) {
            if (onRequestAudioUrl) await onRequestAudioUrl();
            return;
        }
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            try { await audio.play(); } catch (e) { /* autoplay rejection */ }
        } else {
            audio.pause();
        }
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        const canvas = canvasRef.current;
        if (!audio || !canvas || !duration) return;
        const rect = canvas.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        audio.currentTime = Math.max(0, Math.min(duration, ratio * duration));
    };

    const handleDownloadAudio = () => {
        if (!audioUrl) return;
        const a = document.createElement('a');
        a.href = audioUrl;
        a.download = `call-recording.wav`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const hasPeaks = peaks && ((peaks.left && peaks.left.length) || (peaks.right && peaks.right.length));

    return (
        <Box sx={{
            bgcolor: 'action.hover',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 1, p: 2,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Recording</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {fmt(currentTime)} / {fmt(duration)}
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', height: 140, cursor: hasPeaks ? 'pointer' : 'default' }} onClick={handleSeek}>
                <canvas
                    ref={canvasRef}
                    style={{ width: '100%', height: '100%', display: 'block' }}
                />
                {!hasPeaks && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.disabled">
                            Waveform unavailable for this call
                        </Typography>
                    </Box>
                )}
                <Box
                    ref={playheadRef}
                    sx={{
                        position: 'absolute',
                        top: 0, bottom: 0, left: 0,
                        width: '2px',
                        bgcolor: 'text.primary',
                        opacity: 0.7,
                        pointerEvents: 'none',
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                <Tooltip title={audioUrlError ? 'Audio unavailable' : (playing ? 'Pause' : 'Play')}>
                    <span>
                        <IconButton
                            onClick={handlePlayPause}
                            disabled={audioUrlLoading || audioUrlError}
                            color="primary"
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                width: 40, height: 40,
                                '&:hover': { bgcolor: 'primary.dark' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                            }}
                        >
                            {playing ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAudio}
                        disabled={!audioUrl}
                    >
                        Audio
                    </Button>
                </Box>
            </Box>

            {audioUrl && (
                <audio ref={audioRef} src={audioUrl} preload="metadata" style={{ display: 'none' }} />
            )}
        </Box>
    );
};

export default WaveformPlayer;
